import { useState } from 'react'
import { DashboardLayout } from '../../components/layout'
import { StatCard, Button, Modal } from '../../components/ui'
import { UserTable, InviteForm, UserEditForm } from '../../features/users'
import { useUsers } from '../../hooks/useUsers'
import { useBusinesses } from '../../hooks/useBusinesses'
import { useAuth } from '../../context/AuthContext'
import { useToast } from '../../context/ToastContext'
import { db } from '../../lib/supabase'
import LoadingSpinner from '../../components/ui/LoadingSpinner'

function UsersPage() {
  const { users, invitations, counts, loading, updateUserRole, updateUser, getUserBusinessAccess, updateUserBusinessAccess, inviteUser, revokeInvitation, deleteUser, refetch, refetchInvitations } = useUsers()
  const { businesses } = useBusinesses()
  const { user, profile } = useAuth()
  const toast = useToast()

  const [showInviteModal, setShowInviteModal] = useState(false)
  const [inviteLoading, setInviteLoading] = useState(false)

  const [showEditModal, setShowEditModal] = useState(false)
  const [editUser, setEditUser] = useState(null)
  const [editLoading, setEditLoading] = useState(false)
  const [editUserBusinessIds, setEditUserBusinessIds] = useState([])

  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteTargetUser, setDeleteTargetUser] = useState(null)
  const [deleteConfirmName, setDeleteConfirmName] = useState('')
  const [deleteLoading, setDeleteLoading] = useState(false)

  const handleUpdateRole = async (userId, role) => {
    const { error } = await updateUserRole(userId, role)
    if (error) {
      toast.error('Failed to update user role')
      return
    }
    toast.success('User role updated')
    refetch()
  }

  const handleInvite = async (formData) => {
    setInviteLoading(true)

    const { data, error } = await inviteUser(
      formData.email,
      formData.role,
      formData.name,
      user?.id
    )

    if (error) {
      toast.error(`Failed to send invitation: ${error}`)
      setInviteLoading(false)
      return
    }

    // Grant business access if businesses were selected
    if (formData.businessIds.length > 0 && data?.user?.id) {
      for (const businessId of formData.businessIds) {
        await db.grantBusinessAccess(data.user.id, businessId, 'member', user?.id)
      }
    }

    toast.success(`Magic link sent to ${formData.email}`)
    setInviteLoading(false)
    setShowInviteModal(false)
    refetchInvitations()
  }

  const handleRevokeInvitation = async (invitationId, email) => {
    const { error } = await revokeInvitation(invitationId)
    if (error) {
      toast.error('Failed to revoke invitation')
      return
    }
    toast.success(`Invitation to ${email} revoked`)
  }

  const handleOpenEdit = async (userToEdit) => {
    setEditUser(userToEdit)
    setEditUserBusinessIds([])
    setShowEditModal(true)

    // Fetch user's current business access
    const { data: businessIds, error } = await getUserBusinessAccess(userToEdit.id)
    if (!error) {
      setEditUserBusinessIds(businessIds)
    }
  }

  const handleEditSubmit = async (formData) => {
    setEditLoading(true)

    // Update user profile (name, role)
    const { error: userError } = await updateUser(editUser.id, {
      name: formData.name,
      role: formData.role,
    })

    if (userError) {
      toast.error('Failed to update user')
      setEditLoading(false)
      return
    }

    // Update business access
    const { error: accessError } = await updateUserBusinessAccess(
      editUser.id,
      formData.businessIds,
      user?.id
    )

    if (accessError) {
      toast.error('Failed to update business access')
      setEditLoading(false)
      return
    }

    toast.success('User updated successfully')
    setEditLoading(false)
    setShowEditModal(false)
    setEditUser(null)
    refetch()
  }

  const handleOpenDeleteModal = (userToDelete) => {
    setDeleteTargetUser(userToDelete)
    setDeleteConfirmName('')
    setShowDeleteModal(true)
  }

  const handleDeleteUser = async () => {
    if (!deleteTargetUser) return

    const expectedName = deleteTargetUser.name || deleteTargetUser.email
    if (deleteConfirmName !== expectedName) {
      toast.error('Name does not match. Please type the exact name to confirm.')
      return
    }

    setDeleteLoading(true)

    const { error } = await deleteUser(deleteTargetUser.id)

    if (error) {
      toast.error(`Failed to delete user: ${error.message || error}`)
      setDeleteLoading(false)
      return
    }

    toast.success(`User ${expectedName} has been deleted`)
    setDeleteLoading(false)
    setShowDeleteModal(false)
    setDeleteTargetUser(null)
    setDeleteConfirmName('')
  }

  const pendingInvitations = invitations.filter(i => i.status === 'pending')

  if (loading) {
    return (
      <DashboardLayout title="Users">
        <LoadingSpinner.Overlay message="Loading users..." />
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout
      title="Users"
      actions={
        <Button onClick={() => setShowInviteModal(true)}>
          + Invite User
        </Button>
      }
    >
      {/* Stats */}
      <div className="grid grid-4 mb-8">
        <StatCard
          label="Total Users"
          value={counts.total}
        />
        <StatCard
          label="Admins"
          value={counts.admins}
        />
        <StatCard
          label="Members"
          value={counts.members}
        />
        <StatCard
          label="Pending Invites"
          value={counts.pendingInvitations}
        />
      </div>

      {/* Pending Invitations */}
      {pendingInvitations.length > 0 && (
        <div className="card mb-6">
          <div className="card-header">
            <h3>Pending Invitations</h3>
          </div>
          <div className="card-body">
            <table className="table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Invited By</th>
                  <th>Sent</th>
                  <th>Expires</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingInvitations.map(invitation => (
                  <tr key={invitation.id}>
                    <td>{invitation.email}</td>
                    <td>{invitation.name || '-'}</td>
                    <td>
                      <span className={`badge badge-${invitation.role === 'admin' ? 'warning' : 'default'}`}>
                        {invitation.role}
                      </span>
                    </td>
                    <td>{invitation.inviter?.name || '-'}</td>
                    <td>{new Date(invitation.created_at).toLocaleDateString()}</td>
                    <td>{new Date(invitation.expires_at).toLocaleDateString()}</td>
                    <td>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleRevokeInvitation(invitation.id, invitation.email)}
                      >
                        Revoke
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* User Table */}
      <UserTable
        users={users}
        onUpdateRole={handleUpdateRole}
        onEdit={handleOpenEdit}
        onDelete={handleOpenDeleteModal}
        currentUserId={user?.id}
      />

      {/* Invite Modal */}
      <Modal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        title="Invite User"
      >
        <InviteForm
          onSubmit={handleInvite}
          onCancel={() => setShowInviteModal(false)}
          loading={inviteLoading}
          businesses={businesses}
        />
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false)
          setEditUser(null)
        }}
        title="Edit User"
      >
        {editUser && (
          <UserEditForm
            user={editUser}
            onSubmit={handleEditSubmit}
            onCancel={() => {
              setShowEditModal(false)
              setEditUser(null)
            }}
            loading={editLoading}
            businesses={businesses}
            initialBusinessIds={editUserBusinessIds}
          />
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false)
          setDeleteTargetUser(null)
          setDeleteConfirmName('')
        }}
        title="Delete User"
      >
        {deleteTargetUser && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div className="p-4 rounded-lg" style={{ background: 'var(--error-light)', border: '1px solid var(--error)' }}>
              <p className="font-medium mb-2" style={{ color: 'var(--error)' }}>
                This action cannot be undone.
              </p>
              <p className="text-sm">
                This will permanently delete the user <strong>{deleteTargetUser.name || deleteTargetUser.email}</strong> and remove all their access to businesses.
              </p>
            </div>

            <div>
              <label className="form-label">
                Type <strong>{deleteTargetUser.name || deleteTargetUser.email}</strong> to confirm:
              </label>
              <input
                type="text"
                className="form-input"
                value={deleteConfirmName}
                onChange={(e) => setDeleteConfirmName(e.target.value)}
                placeholder="Type the user's name to confirm"
                autoComplete="off"
              />
            </div>

            <div className="flex gap-3 justify-end">
              <Button
                variant="ghost"
                onClick={() => {
                  setShowDeleteModal(false)
                  setDeleteTargetUser(null)
                  setDeleteConfirmName('')
                }}
                disabled={deleteLoading}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleDeleteUser}
                disabled={deleteLoading || deleteConfirmName !== (deleteTargetUser.name || deleteTargetUser.email)}
              >
                {deleteLoading ? 'Deleting...' : 'Delete User'}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </DashboardLayout>
  )
}

export default UsersPage
